import { supabase, syncRealtimeAuth } from '@/lib/supabase';
import { logger } from '@/lib/logger';
import type { AuthUser, LoginCredentials } from '@/types/auth';

const CARE_STAFF_PROFILE_MAX_ATTEMPTS = 3;
const CARE_STAFF_PROFILE_RETRY_DELAYS_MS = [0, 150, 400] as const;

function wait(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
}

async function resolveCareStaffProfile(userId: string): Promise<{ facilityId: string | null; careStaffId: string | null }> {
    for (let attempt = 0; attempt < CARE_STAFF_PROFILE_MAX_ATTEMPTS; attempt += 1) {
        const { data, error } = await supabase
            .from('care_staff')
            .select('id, facility_id')
            .eq('user_id', userId)
            .eq('is_active', true)
            .maybeSingle();

        const resolvedProfile = {
            facilityId: data?.facility_id ?? null,
            careStaffId: data?.id ?? null,
        };

        const hasResolvedProfile = !!resolvedProfile.facilityId && !!resolvedProfile.careStaffId;
        const isLastAttempt = attempt === CARE_STAFF_PROFILE_MAX_ATTEMPTS - 1;

        if (hasResolvedProfile || isLastAttempt) {
            if (error) {
                logger.warn('[AuthService] Fehler beim Laden des Care-Profils:', error.message);
            } else if (!hasResolvedProfile) {
                logger.warn('[AuthService] Care-Profil ohne Facility-Kontext geladen', { userId, attempt: attempt + 1 });
            }

            return resolvedProfile;
        }

        logger.warn('[AuthService] Care-Profil unvollständig, neuer Versuch', {
            userId,
            attempt: attempt + 1,
            error: error?.message ?? null,
        });

        await wait(CARE_STAFF_PROFILE_RETRY_DELAYS_MS[attempt + 1] ?? 0);
    }

    return { facilityId: null, careStaffId: null };
}

/**
 * Maps a Supabase session user to our AuthUser type.
 * Only allows users with role = 'care'.
 */
export async function mapSessionToAuthUser(
    supabaseUser: { id: string; email?: string; user_metadata?: Record<string, unknown> },
): Promise<AuthUser | null> {
    const metadata = supabaseUser.user_metadata ?? {};
    const role = (metadata['role'] as string) || 'care';

    if (role !== 'care') {
        logger.warn('[AuthService] Benutzer hat nicht die Rolle "care":', role);
        return null;
    }

    const careStaffProfile = await resolveCareStaffProfile(supabaseUser.id);

    return {
        id: supabaseUser.id,
        email: supabaseUser.email ?? '',
        role: 'care',
        facilityId: careStaffProfile.facilityId ?? undefined,
        careStaffId: careStaffProfile.careStaffId ?? undefined,
        displayName: (metadata['full_name'] as string) || (metadata['name'] as string) || undefined,
        avatarUrl: (metadata['avatar_url'] as string) || undefined,
    };
}

/**
 * Sign in with email + password. Returns null if login fails or user is not care staff.
 */
export async function login(credentials: LoginCredentials): Promise<AuthUser | null> {
    const { data, error } = await supabase.auth.signInWithPassword({
        email: credentials.email,
        password: credentials.password,
    });

    if (error) {
        logger.error('[AuthService] Login fehlgeschlagen:', error.message);
        throw new Error(error.message);
    }

    await syncRealtimeAuth(data.session?.access_token ?? null);

    if (!data.user) return null;

    return mapSessionToAuthUser(data.user);
}

/**
 * Restore session from storage. Returns null if no valid session exists.
 */
export async function restoreSession(): Promise<AuthUser | null> {
    const { data, error } = await supabase.auth.getSession();

    if (error) {
        logger.warn('[AuthService] Session-Restore fehlgeschlagen:', error.message);
        return null;
    }

    if (!data.session?.user) return null;

    return mapSessionToAuthUser(data.session.user);
}

/**
 * Sign out the current user.
 */
export async function logout(): Promise<void> {
    const { error } = await supabase.auth.signOut();
    if (error) {
        logger.warn('[AuthService] Logout-Fehler:', error.message);
    }
}
